<template>
  <router-view v-slot="{ Component }">
    <keep-alive>
      <component :is="Component" v-if="$route.meta.keepAlive" />
    </keep-alive>
    <component :is="Component" v-if="!$route.meta.keepAlive" />
  </router-view>

  <sound-manager />

  <van-dialog v-model:show="authDialogVisible">
    <div style="padding: 24px 26px">
      {{ authDialogMessage }}
    </div>
  </van-dialog>
</template>

<script>
import { defineComponent } from "vue";
import { mapState } from "vuex";
import { addTokenInterceptor } from "@/utils/http";
import {
  getCompanyInfo,
  getWorkerList,
  getWorkerPermission,
  reportBug,
} from "@/api/common";
import { format, parseISO } from "date-fns";
import { Dialog, Toast } from "vant";
import SoundManager from "@/components/SoundManager.vue";
import CommetFactory from "@/api/CommetFactory";
import { ERROR_TYPES } from "@/enums/error-types";
import websocketFactory from "@/api/websocketFactory";
import { filter, map, catchError, tap } from "rxjs/operators";
import "vant/es/dialog/style";
import { mobileActive } from "@/api/auth";

export default defineComponent({
  components: {
    [Dialog.Component.name]: Dialog.Component,
    SoundManager,
  },

  created() {
    addTokenInterceptor();
    this.initRealtimeConnections();
    document.addEventListener("visibilitychange", this.handleVisibilityChange);
    window.addEventListener("appForeground", this.handleAppForeground);

    this.$store.dispatch("GET_APPS_LIST");
    this.$store.dispatch("UPDATE_BADGE");

    this.getWorkerInfo();
    this.getPermission();
    this.getBillingInfo();
  },

  mounted() {
    const loginName = localStorage.getItem("loginname") || "";

    this.isTestClinet =
      loginName.includes("1003@xmlbjj") ||
      loginName.includes("1001@yqaii") ||
      loginName.includes("1001@dcxi") ||
      loginName.includes("1002@yqaii") ||
      loginName.includes("1013@yqaii");
    this.lastRafTime = performance.now();
    this.visibilityLoopRafId = requestAnimationFrame(this.checkVisibilityLoop);
  },

  beforeUnmount() {
    document.removeEventListener(
      "visibilitychange",
      this.handleVisibilityChange,
    );
    window.removeEventListener("appForeground", this.handleAppForeground);
    if (this.visibilityLoopRafId) {
      cancelAnimationFrame(this.visibilityLoopRafId);
      this.visibilityLoopRafId = null;
    }
    this.pauseRealtimeConnections();
    this.wsSubscription?.unsubscribe();
    this.wsSubscription = null;
  },

  data() {
    return {
      lastRafTime: performance.now(),
      backgroundThreshold: 2000,
      visibilityLoopRafId: null,

      expireDate: "",
      authDialogVisible: false,
      authDialogMessage: "",

      expireDialogVisible: false,
      expireDialogMessage: "",
      commetInstance: null,
      wsInstance: null,
      wsSubscription: null,

      isTestClinet: false,
      loginName: localStorage.getItem("loginname") || "",
    };
  },

  computed: mapState({
    failure: (state) => state.auth.failure,
  }),

  watch: {
    authDialogVisible(val) {
      if (!val) {
        this.$store.commit("AUTHENTICATE_FAILURE", "");
      }
    },

    failure() {
      console.log("failure", this.failure);
      this.$store.commit("UPDATE_DUTY_STATUS", false);
      this.$store.commit("UPDATE_PAGE_RECONNECTING_STATUS", false);

      if (this.failure === ERROR_TYPES.INVALID_TOKEN) {
        this.authDialogMessage = "您的登录链接已失效，请返回重试";
        this.authDialogVisible = true;
      }

      if (this.failure === ERROR_TYPES.USE_MOBILE_PERMISSION_FAIL) {
        this.authDialogMessage =
          "您已被禁止使用手机值班功能，请联系管理员为您开启";
        this.authDialogVisible = true;
      }

      if (this.failure === ERROR_TYPES.WORKER_NOT_ONLINE) {
        this.authDialogMessage = "您已将值班状态设置成离线，可在设置页面修改。";
        this.authDialogVisible = true;
      }
    },
  },

  methods: {
    getReadableTimestamp() {
      return new Date().toLocaleString("zh-CN", { hour12: false });
    },

    initRealtimeConnections() {
      this.commetInstance = CommetFactory(this.$store);
      this.commetInstance.connect();

      this.wsInstance = websocketFactory();

      if (!this.wsSubscription || this.wsSubscription.closed) {
        this.wsSubscription = this.wsInstance.messages$
          .pipe(
            filter((rows) => rows.data),
            map((rows) => {
              return {
                ...rows.data,
                microtime: rows.microtime,
              };
            }),
            catchError((error) => {
              throw error;
            }),
            tap({
              error: (error) => console.log("[Live component] Error:", error),
              complete: () => console.log("[Live component] Connection Closed"),
            }),
          )
          .subscribe((res) => {
            this.$store.dispatch("UPDATE_UNREAD_COUNT", res?.body?.unread || 0);
          });
      }

      this.wsInstance.connect(localStorage.getItem("token"));
    },

    pauseRealtimeConnections() {
      this.commetInstance?.stop();

      if (this.wsInstance && !this.wsInstance.socket$?.closed) {
        this.wsInstance.close();
      }
    },

    resumeRealtimeConnections() {
      this.commetInstance = this.commetInstance || CommetFactory(this.$store);
      this.wsInstance = this.wsInstance || websocketFactory();

      mobileActive();

      this.commetInstance.connect();
      this.wsInstance.connect(localStorage.getItem("token"));
    },

    triggerResumeConnections(source) {
      this.commetInstance = this.commetInstance || CommetFactory(this.$store);

      // 用 Commet 自身的 connecting 做守卫，而不是 store 里的 isPageReconnecting：
      // 后者之前只在连接成功时复位，失败或重复触发（appForeground 与 visibilitychange 先后到达）时
      // 会一直卡在 true，把 appForeground / visibilitychange / raf-fallback 三条恢复路径全部拦掉。
      // 已连接时 connect() / ws.connect() 本身幂等，可以放心走下去。
      if (this.commetInstance.connecting) {
        return;
      }

      if (this.isTestClinet) {
        reportBug({
          message: `[${this.getReadableTimestamp()}] resume from ${source}`,
          user: localStorage.getItem("loginname"),
        }).subscribe(() => {});
      }

      console.log(
        `[${this.getReadableTimestamp()}] resumeRealtimeConnections from ${source}`,
      );
      this.resumeRealtimeConnections();
    },

    handleAppForeground(event) {
      console.log(
        `[${this.getReadableTimestamp()}] appForeground event`,
        event?.detail,
      );

      if (this.isTestClinet) {
        reportBug({
          message: `[${this.getReadableTimestamp()}] appForeground`,
          user: localStorage.getItem("loginname"),
        }).subscribe(() => {});
      }

      this.triggerResumeConnections("appForeground");
    },

    checkVisibilityLoop(currentTime) {
      const deltaTime = currentTime - this.lastRafTime;
      if (deltaTime > this.backgroundThreshold) {
        console.log("raf threshold check", this.getReadableTimestamp());

        if (this.isTestClinet) {
          reportBug({
            message: `[${this.getReadableTimestamp()}] raf-threshold-check`,
            user: localStorage.getItem("loginname"),
          }).subscribe(() => {});
        }

        setTimeout(() => {
          if(document.visibilityState === "hidden") {
            return;
          }

          this.commetInstance =
            this.commetInstance || CommetFactory(this.$store);

          if (
            !this.commetInstance.connected &&
            !this.commetInstance.connecting
          ) {
            reportBug({
              message: `[${this.getReadableTimestamp()}] raf-fallback`,
              user: localStorage.getItem("loginname"),
              visibleState: document.visibilityState,
            }).subscribe(() => {
              console.log(
                "report bug response time",
                this.getReadableTimestamp(),
              );
            });

            if (this.loginName.includes("yqaii")) {
              Toast.error("请截图发送给开发人员，谢谢！！！");
            }
            this.triggerResumeConnections("raf-fallback");
          }
        }, 1500);
      }

      this.lastRafTime = currentTime;
      this.visibilityLoopRafId = requestAnimationFrame(
        this.checkVisibilityLoop,
      );
    },

    handleVisibilityChange() {
      const currentState = document.visibilityState;

      console.log(
        `[${this.getReadableTimestamp()}] visibility change: ${currentState}`,
      );
      if (this.isTestClinet) {
        reportBug({
          message: `[${this.getReadableTimestamp()}] visibility change: ${currentState}`,
          user: localStorage.getItem("loginname"),
        }).subscribe(() => {});
      }
      if (currentState === "hidden") {
        console.log(
          `[${this.getReadableTimestamp()}] pauseRealtimeConnections version 2`,
        );

        this.pauseRealtimeConnections();
        return;
      }

      if (currentState === "visible") {
        this.triggerResumeConnections("visibilitychange");
      }
    },

    closeAuthDialog() {
      this.$store.commit("AUTHENTICATE_FAILURE", "");
      return true;
    },

    getWorkerInfo() {
      getWorkerList().subscribe((workerList) => {
        const workerName = localStorage.getItem("workername");
        const worker = workerList.find((w) => {
          return w.name === workerName;
        });
        if (worker) {
          localStorage.setItem("worker_data", JSON.stringify(worker));
        }
      });
    },

    getPermission() {
      getWorkerPermission(localStorage.getItem("workername")).subscribe(
        ({ privileges, worker }) => {
          const platformPermissions = {
            web: privileges.channels.web?.length > 0 ||  worker.is_admin,
            wechat: privileges.channels.wechat?.length > 0 || worker.is_admin,
            douyin: privileges.channels.douyin?.length > 0 || worker.is_admin,
            kuaishou: privileges.channels.kuaishou?.length > 0 || worker.is_admin,
            redbook: privileges.channels.redbook?.length > 0 || worker.is_admin,
            weibo: privileges.channels.weibo?.length > 0 || worker.is_admin,
          };

          const platformPermissionStr = Object.entries(platformPermissions)
            .filter(([_, value]) => value)
            .map(([key]) => key)
            .join(",");

          localStorage.setItem("platform_permissions", platformPermissionStr);
          localStorage.setItem("is_admin", worker.is_admin ? "true" : "false");
          localStorage.setItem(
            "access_conversation",
            privileges.perms.view_chat || worker.is_admin
              ? "true"
              : "false",
          );
        },
      );
    },

    getBillingInfo() {
      getCompanyInfo().subscribe((res) => {
        localStorage.setItem("company_code", res.code);
        localStorage.setItem("advisor", JSON.stringify(res.advisor));

        let version = res.payment.verson;
        let checkArr =
          version === "ai2024"
            ? ["service_time_end", "wxapp_time_end", "wbapp_time_end"]
            : ["service_time_end"];

        checkArr.forEach((prop) => {
          if (res.payment[prop]) {
            this.checkProp(prop, res);
          }
        });
      });
    },

    checkProp(prop, res) {
      let servceTimeEndText =
        res.payment.version === "ai2024"
          ? "购买的【AI智能客服】"
          : "的云雀席位";

      const name = {
        service_time_end: servceTimeEndText,
        wxapp_time_end: "增加的微信公众号/小程序/微信客服/微信小店配额",
        wbapp_time_end: "增加的抖音/快手/小红书/微博/头条咨询页配额",
      };

      const now = new Date();
      const endTime = parseISO(res.payment[prop]);
      this.expireDate = format(endTime, "yyyy-MM-dd");

      const dayDifference = Math.ceil((endTime - now) / 86400000);
      if (dayDifference <= 0) {
        Dialog.alert({
          message: `尊敬的用户，\n您${name[prop]}已于${this.expireDate}到期，请使用电脑登陆管理员账号充值续费，以免影响您的使用`,
        });
      } else if (dayDifference <= 3) {
        Dialog.alert({
          message: `尊敬的用户，\n您${name[prop]}即将于${this.expireDate}到期，请使用电脑登陆管理员账号充值续费，以免影响您的使用`,
        });
      }
    },
  },
});
</script>
